// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { useIsDesktop } from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import type { ReactNode } from 'react';

export const PANEL_TRANSITION = {
  type: 'spring',
  stiffness: 380,
  damping: 38,
  mass: 0.85,
} as const;

const PANEL_WIDTH = 288; // w-72

const PANEL_CARD_CLASSES =
  'border-ds-border-neutral-subtle-default bg-ds-bg-neutral-default-default flex flex-col overflow-hidden rounded-3xl border border-solid';

interface ResponsivePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  side: 'left' | 'right';
  motionKey: string;
  ariaLabelledBy: string;
  children: ReactNode;
}

/**
 * Shared shell for the remote side panels. On desktop (lg+) the panel
 * participates in the page flex layout as a width-animated column, keeping
 * the main content interactive. Below lg it becomes a fixed overlay with a
 * blurred backdrop that closes the panel on click.
 */
export function ResponsivePanel({
  open,
  onOpenChange,
  side,
  motionKey,
  ariaLabelledBy,
  children,
}: ResponsivePanelProps) {
  const reduceMotion = useReducedMotion();
  const isDesktop = useIsDesktop();
  const transition = reduceMotion ? { duration: 0 } : PANEL_TRANSITION;

  return (
    <>
      {open && !isDesktop && (
        <div
          className="fixed inset-0 z-40 bg-dialog-overlay-scrim backdrop-blur-sm lg:hidden"
          aria-hidden
          onClick={() => onOpenChange(false)}
        />
      )}
      <AnimatePresence initial={false}>
        {open ? (
          isDesktop ? (
            <motion.div
              key={`${motionKey}-column`}
              className="relative h-full shrink-0 overflow-hidden"
              initial={reduceMotion ? false : { width: 0 }}
              animate={{ width: PANEL_WIDTH }}
              exit={reduceMotion ? undefined : { width: 0 }}
              transition={transition}
            >
              <aside
                aria-labelledby={ariaLabelledBy}
                className={cn(
                  PANEL_CARD_CLASSES,
                  'absolute inset-y-0 w-72',
                  side === 'left' ? 'left-0' : 'right-0'
                )}
              >
                {children}
              </aside>
            </motion.div>
          ) : (
            <motion.aside
              key={`${motionKey}-overlay`}
              role="dialog"
              aria-modal="false"
              aria-labelledby={ariaLabelledBy}
              className={cn(
                PANEL_CARD_CLASSES,
                'fixed bottom-2 top-2 z-50 w-72 max-w-72 shadow-2xl',
                side === 'left' ? 'left-2' : 'right-2'
              )}
              initial={
                reduceMotion ? false : { x: side === 'left' ? '-100%' : '100%' }
              }
              animate={{ x: 0 }}
              exit={
                reduceMotion
                  ? undefined
                  : { x: side === 'left' ? '-100%' : '100%' }
              }
              transition={transition}
            >
              {children}
            </motion.aside>
          )
        ) : null}
      </AnimatePresence>
    </>
  );
}
